import React, { useState } from "react";
import { toast } from "react-toastify";
import { getToken } from "../../services/Token";
import { useSetReactionsMutation } from "../../api/engageApi";
import ReactionsDialog from "./ReactionsDialog";
import {
  REACTION_SET,
  canGiveReactions,
  nextEmojiSet,
  normalizeReactions,
  previewReactions,
  reactionSegment,
  totalReactionCount,
} from "./constants";

const ReactionBar = ({ post, onReacted }) => {
  const segment = reactionSegment(post.type);
  const [setReactions, { isLoading }] = useSetReactionsMutation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [whoOpen, setWhoOpen] = useState(false);

  const mayReact = canGiveReactions(getToken("roles"));

  if (!segment) return null;

  const reactions = normalizeReactions(post.reactions);
  const hasAny = totalReactionCount(reactions) > 0;

  const toggle = async (emoji) => {
    if (isLoading) return;
    setPickerOpen(false);
    const previous = reactions;
    onReacted(post.id, previewReactions(previous, emoji));
    try {
      const fresh = await setReactions({
        segment,
        postId: post.id,
        emoji: nextEmojiSet(previous, emoji),
      }).unwrap();
      onReacted(post.id, normalizeReactions(fresh));
    } catch (error) {
      onReacted(post.id, previous);
      toast.error(error?.data?.message || "Could not update your reaction");
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 px-4 py-3">
        {mayReact && (
          <button
            type="button"
            disabled={isLoading}
            onClick={() => setPickerOpen((open) => !open)}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-500 hover:border-blue-600 hover:text-blue-600 disabled:opacity-60"
          >
            <span aria-hidden="true">&#128077;</span>
            React
          </button>
        )}

        {reactions.map((row) =>
          mayReact ? (
            <button
              key={row.emoji}
              type="button"
              disabled={isLoading}
              onClick={() => toggle(row.emoji)}
              aria-label={`${row.count} reacted with ${row.emoji}`}
              className={
                row.reactedByMe
                  ? "flex items-center gap-1.5 rounded-full border border-blue-600 bg-blue-secondery-1 px-2.5 py-1 text-sm font-semibold text-blue-700 disabled:opacity-60"
                  : "flex items-center gap-1.5 rounded-full border border-transparent bg-gray-100 px-2.5 py-1 text-sm font-semibold text-gray-600 hover:border-gray-300 disabled:opacity-60"
              }
            >
              <span>{row.emoji}</span>
              <span className="text-xs tabular-nums">{row.count}</span>
            </button>
          ) : (
            // A span, not a disabled button - nothing here is clickable for an
            // administrative role, so it should not look like a dead control.
            <span
              key={row.emoji}
              aria-label={`${row.count} reacted with ${row.emoji}`}
              className="flex items-center gap-1.5 rounded-full border border-transparent bg-gray-100 px-2.5 py-1 text-sm font-semibold text-gray-600"
            >
              <span>{row.emoji}</span>
              <span className="text-xs tabular-nums">{row.count}</span>
            </span>
          )
        )}

        {hasAny && (
          <button
            type="button"
            onClick={() => setWhoOpen(true)}
            className="ml-auto text-xs text-gray-400 underline hover:text-blue-600"
          >
            who reacted
          </button>
        )}
      </div>

      {pickerOpen && mayReact && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-3">
          {REACTION_SET.map((emoji) => (
            <button
              key={emoji}
              type="button"
              disabled={isLoading}
              onClick={() => toggle(emoji)}
              aria-label={`React with ${emoji}`}
              className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-lg leading-none hover:border-blue-600 hover:bg-blue-secondery-1 disabled:opacity-60"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      <ReactionsDialog
        open={whoOpen}
        segment={segment}
        postId={post.id}
        onClose={() => setWhoOpen(false)}
      />
    </>
  );
};

export default ReactionBar;
